import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { useUserMsp } from "../hooks/useUserMsp";
import { useAppSelector } from "../hooks";
import SupervisorApi from "../apis/supervisorApi";
import { getApiErrorMessage } from "../utils/apiErrorMessage";
import { isNinjaOne, isAutotask } from "../utils/mspUtils";
import {
  getTicketClientLabel,
  getTicketStatusLabel,
  getTicketTheoExecuteUrl,
} from "./automatableDashboardShared";
import { psaTicketIsUnassigned } from "./psaTicketAssigneeHelpers";

const FETCH_PAGE_SIZE = 650;

function priorityRank(priority: unknown): number {
  const s = String(priority ?? "").toLowerCase();
  if (/critical|^p\s*1|priority\s*1/.test(s)) return 5;
  if (/high|^p\s*2|priority\s*2/.test(s)) return 4;
  if (/medium|^p\s*3|med\b/.test(s)) return 3;
  if (/low|^p\s*4/.test(s)) return 2;
  if (/plan|^p\s*5/.test(s)) return 1;
  return 0;
}

function sortTicketsForWorkload<T extends Record<string, unknown>>(tickets: T[]): T[] {
  return [...tickets].sort((a, b) => {
    const pr = priorityRank(b["priority"]) - priorityRank(a["priority"]);
    if (pr !== 0) return pr;
    const da = Date.parse(String(a["created_at"] ?? a["updated_at"] ?? ""));
    const db = Date.parse(String(b["created_at"] ?? b["updated_at"] ?? ""));
    if (Number.isFinite(da) && Number.isFinite(db)) return db - da;
    return 0;
  });
}

async function fetchTicketsPage(
  supervisorApi: SupervisorApi,
  mspType: string,
  filters: Parameters<SupervisorApi["getSuperopsTickets"]>[2],
  sort: { attribute: string; order: "ASC" | "DESC" }[],
  ninjaBoard?: { id: number; name: string } | null
): Promise<{ tickets?: unknown[] }> {
  if (isNinjaOne(mspType)) {
    if (!ninjaBoard?.id) return { tickets: [] };
    return supervisorApi.getNinjaoneTickets(1, FETCH_PAGE_SIZE, {
      ...filters,
      boardId: ninjaBoard.id,
      groupName: ninjaBoard.name,
    }, sort);
  }
  if (isAutotask(mspType)) {
    return supervisorApi.getAutotaskTickets(1, FETCH_PAGE_SIZE, filters, sort);
  }
  return supervisorApi.getSuperopsTickets(1, FETCH_PAGE_SIZE, filters, sort);
}

export interface DashboardMyFocusTabProps {
  /** Legacy props kept for compatibility; My focus uses live PSA ``GET /tickets`` only. */
  dashboardCacheDay?: string | null;
  automatableLegacy?: boolean;
}

const DashboardMyFocusTab: React.FC<DashboardMyFocusTabProps> = () => {
  const navigate = useNavigate();
  const { user } = useAuth0();
  const { userMspInfo } = useUserMsp();
  const ninjaoneBoardList = useAppSelector(
    (state: any) => state.supervisor?.ninjaoneBoardList ?? []
  );
  const supervisorApi = useMemo(() => new SupervisorApi(), []);

  const [assignedTickets, setAssignedTickets] = useState<any[]>([]);
  const [assignedLoading, setAssignedLoading] = useState(true);
  const [assignedError, setAssignedError] = useState<string | null>(null);

  const [unassignedTickets, setUnassignedTickets] = useState<any[]>([]);
  const [pipelineLoading, setPipelineLoading] = useState(true);
  const [pipelineError, setPipelineError] = useState<string | null>(null);

  const psaId = userMspInfo?.psa_user_id != null ? String(userMspInfo.psa_user_id).trim() : "";

  const allTicketsBoard = useMemo(() => {
    if (!userMspInfo?.msp_type || !isNinjaOne(userMspInfo.msp_type)) return null;
    const boards = Array.isArray(ninjaoneBoardList) ? ninjaoneBoardList : [];
    return boards.find((b: any) => String(b?.name || "").toLowerCase() === "all tickets") ?? null;
  }, [userMspInfo?.msp_type, ninjaoneBoardList]);

  const pipelineSort = useMemo(
    () => [{ attribute: "updated_at", order: "DESC" as const }],
    []
  );

  const loadAssignedTickets = useCallback(async () => {
    if (!user?.email || !userMspInfo?.msp_type || !psaId) {
      setAssignedTickets([]);
      setAssignedLoading(false);
      setAssignedError(null);
      return;
    }

    if (isNinjaOne(userMspInfo.msp_type) && !allTicketsBoard?.id) {
      setAssignedTickets([]);
      setAssignedLoading(false);
      setAssignedError(null);
      return;
    }

    setAssignedLoading(true);
    setAssignedError(null);

    try {
      const sort = [{ attribute: "created_at", order: "DESC" as const }];
      const baseFilters = {
        assignedTo: psaId,
        includeAllStatuses: true as const,
      };

      const res = await fetchTicketsPage(
        supervisorApi,
        userMspInfo.msp_type,
        baseFilters,
        sort,
        allTicketsBoard
      );

      const raw = Array.isArray(res?.tickets) ? res.tickets : [];
      setAssignedTickets(sortTicketsForWorkload(raw as Record<string, unknown>[]));
    } catch (e: unknown) {
      console.error("[My focus] assigned tickets:", e);
      setAssignedTickets([]);
      setAssignedError(getApiErrorMessage(e, "Could not load assigned tickets."));
    } finally {
      setAssignedLoading(false);
    }
  }, [user?.email, userMspInfo?.msp_type, psaId, allTicketsBoard, supervisorApi]);

  const loadRecentPipeline = useCallback(async () => {
    if (!user?.email || !userMspInfo?.msp_type) {
      setUnassignedTickets([]);
      setPipelineLoading(false);
      setPipelineError(null);
      return;
    }

    if (isNinjaOne(userMspInfo.msp_type) && !allTicketsBoard?.id) {
      setUnassignedTickets([]);
      setPipelineLoading(false);
      setPipelineError(null);
      return;
    }

    setPipelineLoading(true);
    setPipelineError(null);

    try {
      const filters = { includeAllStatuses: true as const };
      const res = await fetchTicketsPage(
        supervisorApi,
        userMspInfo.msp_type,
        filters,
        pipelineSort,
        allTicketsBoard
      );

      const raw = Array.isArray(res?.tickets) ? res.tickets : [];
      const unassigned = raw.filter((t) => psaTicketIsUnassigned(t));
      setUnassignedTickets(sortTicketsForWorkload(unassigned as Record<string, unknown>[]).slice(0, 45));
    } catch (e: unknown) {
      console.error("[My focus] PSA pipeline:", e);
      setUnassignedTickets([]);
      setPipelineError(getApiErrorMessage(e, "Could not load recent PSA tickets."));
    } finally {
      setPipelineLoading(false);
    }
  }, [
    user?.email,
    userMspInfo?.msp_type,
    allTicketsBoard,
    supervisorApi,
    pipelineSort,
  ]);

  const loadAll = useCallback(async () => {
    await Promise.all([loadAssignedTickets(), loadRecentPipeline()]);
  }, [loadAssignedTickets, loadRecentPipeline]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const ninjaNote = userMspInfo?.msp_type && isNinjaOne(userMspInfo.msp_type);

  const dueSoonCount = useMemo(() => {
    const now = Date.now();
    const horizonMs = 36 * 60 * 60 * 1000;
    return assignedTickets.filter((t) => {
      const due =
        t?.due_date ??
        t?.dueDate ??
        t?.customFields?.dueDateTime ??
        t?.start_date ??
        "";
      const ms = Date.parse(String(due));
      return Number.isFinite(ms) && ms >= now && ms <= now + horizonMs;
    }).length;
  }, [assignedTickets]);

  const openTicketRow = (ticket: any) => {
    const id = ticket?.id != null ? String(ticket.id) : "";
    if (!id) return;
    navigate(`/supervisor/tickets/${encodeURIComponent(id)}`);
  };

  const pipelineBusy = pipelineLoading;
  const anyLoading = assignedLoading || pipelineBusy;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshRoundedIcon />}
          disabled={anyLoading}
          onClick={() => {
            void loadAll();
          }}
        >
          Refresh from PSA
        </Button>
      </Box>

      {!psaId && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Ask your MSP admin to set your <strong>PSA User ID</strong> on the Theo Users page so “Assigned to me”
          matches your PSA login correctly.
        </Alert>
      )}
      {ninjaNote && (
        <Alert severity="info" sx={{ mb: 2 }}>
          NinjaOne assigns tickets by agent display name — ensure your Theo <strong>PSA User ID</strong> matches how
          NinjaOne exposes your assignments for best results on this dashboard.
        </Alert>
      )}

      <Grid container spacing={{ xs: 2, sm: 2, md: 2.5 }}>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2, borderRadius: 2, height: "100%" }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Assigned to me
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
              {assignedLoading ? "—" : assignedTickets.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2, borderRadius: 2, height: "100%" }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Due in next ~36h
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
              {assignedLoading ? "—" : dueSoonCount}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2, borderRadius: 2, height: "100%" }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Unassigned (recent PSA window)
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
              {pipelineBusy ? "—" : unassignedTickets.length}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {assignedError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {assignedError}
        </Alert>
      )}
      {pipelineError && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {pipelineError}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mt: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Assigned to me
            </Typography>
            {assignedLoading && (
              <CircularProgress size={22} sx={{ ml: 1.5 }} aria-label="Loading assigned tickets" />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Live data from your PSA via Horizon (<code>GET /tickets</code> with assignee filter). Sorted by priority,
            then age.
          </Typography>
          <TicketWorkloadTable
            tickets={assignedTickets}
            loading={assignedLoading}
            emptyHint={
              psaId ? "No tickets returned for your PSA assignment filter." : "Set PSA User ID to load this list."
            }
            onOpenTicket={openTicketRow}
            showExecuteLink
          />
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mt: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Unassigned — grab next
            </Typography>
            {pipelineBusy && (
              <CircularProgress size={22} sx={{ ml: 1.5 }} aria-label="Loading PSA pipeline" />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Recent tickets from your PSA (sorted by last update, newest first), filtered client-side to rows with no
            resolved assignee — within the newest ~{FETCH_PAGE_SIZE.toLocaleString()} tickets Horizon pulls for your MSP.
          </Typography>
          <TicketWorkloadTable
            tickets={unassignedTickets}
            loading={pipelineBusy}
            emptyHint="No unassigned tickets in the current PSA window. Try Refresh or open Queues for full lists."
            onOpenTicket={openTicketRow}
            showExecuteLink
          />
        </CardContent>
      </Card>
    </Box>
  );
};

function TicketWorkloadTable(props: {
  tickets: any[];
  loading: boolean;
  emptyHint: string;
  onOpenTicket: (t: any) => void;
  showExecuteLink?: boolean;
}) {
  const { tickets, loading, emptyHint, onOpenTicket, showExecuteLink } = props;

  return (
    <TableContainer sx={{ borderRadius: 1, border: "1px solid #EEF2F6" }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow sx={{ bgcolor: "#F8FAFC" }}>
            <TableCell sx={{ fontWeight: 700 }}>Ticket</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
            {showExecuteLink && (
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Theo
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading && tickets.length === 0 && (
            <TableRow>
              <TableCell colSpan={showExecuteLink ? 5 : 4}>
                <Typography variant="body2" color="text.secondary">
                  {emptyHint}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {tickets.map((t) => {
            const idKey = String(t?.id ?? t?.ticketId ?? "");
            const execHref = ticketTheoExecuteHrefForTicket(t);
            return (
              <TableRow
                key={idKey}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => onOpenTicket(t)}
              >
                <TableCell>
                  <Typography sx={{ fontWeight: 700 }} variant="body2">
                    #{ticketDisplayNumberForTicket(t)}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.25 }}>
                    {String(t?.title ?? t?.subject ?? t?.summary ?? "—")}
                  </Typography>
                </TableCell>
                <TableCell>{getTicketClientLabel(t)}</TableCell>
                <TableCell>{getTicketStatusLabel(t)}</TableCell>
                <TableCell>
                  <Chip size="small" label={String(t?.priority ?? "—")} variant="outlined" />
                </TableCell>
                {showExecuteLink && (
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    {execHref ? (
                      <Link href={execHref} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600 }}>
                        Execute
                      </Link>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ticketDisplayNumberForTicket(t: any): string {
  return String(t?.displayId ?? t?.display_id ?? t?.ticketNumber ?? t?.id ?? "").trim() || "—";
}

function ticketTheoExecuteHrefForTicket(t: any): string {
  const id = t?.id != null ? String(t.id) : "";
  return id ? getTicketTheoExecuteUrl(id) : "";
}

export default DashboardMyFocusTab;
